/**
 * R5 · 圈哪改哪 —— mask 画笔(前置门① 实测通过后的落地)。
 *
 * ═══ 一条硬约定:白色 = 要改 ═══════════════════════════════════════════════════
 * 万相 `description_edit_with_mask` 的原生口径就是「白色区域重绘」,全链路统一按这个传
 * (ai 侧 `ImageEditRequest.mask_bytes` 注释同款)。OpenAI 那边是「透明 = 要改」,
 * 真接的时候在 provider 层翻译 —— **不在这里翻**,否则两端各转一次谁也说不清最终是哪个。
 *
 * ═══ 为什么导出的是与原图**同尺寸**的黑白 PNG ═══════════════════════════════════
 * 引擎按像素对齐 mask 与原图。画布为了显示会缩放,所以落笔坐标要按 `naturalWidth/显示宽`
 * 反算回原图坐标系;导出时再按原图尺寸重绘一遍。
 * ★门②实测的教训就在这:mask 形状**决定**重绘形状 —— 矩形 mask 出来就是矩形的一块布。
 *   所以画笔给的是圆头笔刷 + 可调粗细,让用户能贴着轮廓涂,而不是只能拉框。
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Eraser, Paintbrush, RotateCcw } from "lucide-react";

import { cn } from "@/shared/lib/utils";

export interface MaskPainterHandle {
  /**
   * 导出当前 mask(与原图同尺寸的黑白 PNG);没涂 / 擦光了返回 null。
   *
   * ★**只在提交那一刻调一次**,不在每笔结束时往外推 —— 见文件头注的"为什么不预传"。
   */
  exportNow: () => Promise<File | null>;
  /**
   * 外部清空 mask(0925 收尾波 B5):选 OPENAI 家族时局部重绘用不了,父层要能
   * 把已经涂好的红区真的擦掉(不止是清 `maskPainted` 布尔值),避免用户切回万相
   * 时又看到一片"其实早该清空"的旧涂抹。
   */
  reset: () => void;
}

interface Props {
  /** 原图预览 URL(objectURL 或 presigned)。 */
  imageUrl: string;
  /** 涂/没涂发生变化时通知父层(同步,只是给界面用,不参与提交)。 */
  onPaintedChange?: (painted: boolean) => void;
  disabled?: boolean;
  /**
   * 非空 = 入口被禁用的理由(0925 B5:出图模型是 OPENAI 家族时,局部重绘还没接)。
   * 命中时画笔/按钮一律 disabled(与 `disabled` 取或),并把这句话顶替常规的
   * "不涂 = 整图重绘" 提示,让用户知道**为什么**灰着,而不是当作没画出来。
   */
  disabledReason?: string;
}

const BRUSH_SIZES = [24, 48, 96] as const;

export const MaskPainter = forwardRef<MaskPainterHandle, Props>(function MaskPainter(
  { imageUrl, onPaintedChange, disabled, disabledReason },
  ref
) {
  const isDisabled = disabled || !!disabledReason;
  const imgRef = useRef<HTMLImageElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  /** 原图尺寸的离屏 mask(真相在这里;显示层只是它的缩放投影)。 */
  const maskRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  /** 上一个采样点(原图坐标系);拖动时把两点连成一段,而不是留一串断点。 */
  const lastPt = useRef<{ x: number; y: number } | null>(null);
  const [brush, setBrush] = useState<number>(48);
  const [erasing, setErasing] = useState(false);
  const [painted, setPainted] = useState(false);

  /** 图片加载完成 → 按原图尺寸建离屏 mask(全黑 = 什么都不改)。 */
  const onImgLoad = useCallback(() => {
    const img = imgRef.current;
    if (!img) return;
    const off = document.createElement("canvas");
    off.width = img.naturalWidth;
    off.height = img.naturalHeight;
    const ctx = off.getContext("2d")!;
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, off.width, off.height);
    maskRef.current = off;
    setPainted(false);
    onPaintedChange?.(false);
    redraw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onPaintedChange]);

  /** 把离屏 mask 以半透明红叠到显示画布上(所见即将被重绘的区域)。 */
  const redraw = useCallback(() => {
    const c = canvasRef.current;
    const img = imgRef.current;
    const off = maskRef.current;
    if (!c || !img || !off) return;
    c.width = img.clientWidth;
    c.height = img.clientHeight;
    const ctx = c.getContext("2d")!;
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.globalAlpha = 0.45;
    ctx.drawImage(off, 0, 0, c.width, c.height);
    ctx.globalAlpha = 1;
    // 白 → 红:让"要改的区域"在原图上一眼可见(纯白叠在浅色图上看不出来)
    const px = ctx.getImageData(0, 0, c.width, c.height);
    for (let i = 0; i < px.data.length; i += 4) {
      const v = px.data[i];
      px.data[i] = 239;
      px.data[i + 1] = 68;
      px.data[i + 2] = 68;
      px.data[i + 3] = v > 40 ? 150 : 0;
    }
    ctx.putImageData(px, 0, 0);
  }, []);

  useEffect(() => {
    const onResize = () => redraw();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [redraw]);

  /** 离屏 mask 上还有没有"要改"的白像素(阈值与 redraw 的可见判据同源)。 */
  const hasWhite = useCallback((off: HTMLCanvasElement) => {
    const { data } = off.getContext("2d")!.getImageData(0, 0, off.width, off.height);
    for (let i = 0; i < data.length; i += 4) {
      if (data[i] > 40) return true;
    }
    return false;
  }, []);

  /**
   * 提交时导出:没涂 / 擦光了 → null(= 整图重绘,与"没涂"同一条路)。
   *
   * ★这里**不需要**任何令牌/在途闸:导出只在提交那一刻发生一次,调用方 await 它,
   * 期间按钮已经是提交中的禁用态 —— 没有"用户在导出途中又改了 mask"这种窗口。
   * (先前每笔预传的版本被 codex 连抓 5 轮竞态:上传竞态 → 清空卡死 → 导出层竞态 →
   *  两笔共用令牌 → 抬笔到回调的窗口。窗口是**预传这个结构**造出来的,不是补丁不够多。)
   */
  const exportNow = useCallback(
    () =>
      new Promise<File | null>((resolve) => {
        const off = maskRef.current;
        if (!off || !hasWhite(off)) {
          resolve(null);
          return;
        }
        off.toBlob((blob) => {
          resolve(blob ? new File([blob], "mask.png", { type: "image/png" }) : null);
        }, "image/png");
      }),
    [hasWhite]
  );

  const paintAt = useCallback(
    (clientX: number, clientY: number) => {
      const c = canvasRef.current;
      const off = maskRef.current;
      const img = imgRef.current;
      if (!c || !off || !img) return;
      const rect = c.getBoundingClientRect();
      // ★显示坐标 → 原图坐标(画布是缩放显示的,不反算就会画偏)
      const scale = off.width / rect.width;
      const x = (clientX - rect.left) * scale;
      const y = (clientY - rect.top) * scale;
      const ctx = off.getContext("2d")!;
      const color = erasing ? "#000" : "#fff";
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(x, y, (brush * scale) / 2, 0, Math.PI * 2);
      ctx.fill();
      // ★两点之间补一段(codex R5 八轮):pointermove 采样是稀疏的,拖快一点就只剩一串
      //   圆点 —— 用户画的是一条连续的道,引擎收到的是断续的洞。圆头线段把空档补上。
      const prev = lastPt.current;
      if (prev) {
        ctx.strokeStyle = color;
        ctx.lineWidth = brush * scale;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.beginPath();
        ctx.moveTo(prev.x, prev.y);
        ctx.lineTo(x, y);
        ctx.stroke();
      }
      lastPt.current = { x, y };
      if (!erasing && !painted) {
        setPainted(true); // 擦只会减少涂区;擦光后的真值在抬笔时落
        onPaintedChange?.(true);
      }
      redraw();
    },
    [brush, erasing, painted, onPaintedChange, redraw]
  );

  const end = useCallback(() => {
    if (!drawing.current) return;
    drawing.current = false;
    lastPt.current = null; // 下一笔不能从上一笔的结尾连过来
    // 擦光了要退回"没涂"(否则界面显示涂过、提交时导出却是 null,两头对不上)
    const off = maskRef.current;
    const still = !!off && hasWhite(off);
    setPainted(still);
    onPaintedChange?.(still);
  }, [hasWhite, onPaintedChange]);

  const reset = useCallback(() => {
    const off = maskRef.current;
    if (!off) return;
    const ctx = off.getContext("2d")!;
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, off.width, off.height);
    setPainted(false);
    onPaintedChange?.(false);
    redraw();
  }, [redraw, onPaintedChange]);

  useImperativeHandle(ref, () => ({ exportNow, reset }), [exportNow, reset]);

  return (
    <div className="space-y-2" data-mask-painter={painted ? "painted" : "empty"}>
      <div className="flex flex-wrap items-center gap-2 text-[12.5px] text-muted-foreground">
        <span className="font-medium text-foreground">涂出要改的区域</span>
        <span>·涂到哪改到哪,其余像素原样保留</span>
        <div className="ml-auto flex items-center gap-1.5">
          <button
            type="button"
            aria-pressed={!erasing}
            disabled={isDisabled}
            onClick={() => setErasing(false)}
            className={cn(
              "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[12.5px]",
              !erasing ? "border-primary bg-primary/10 text-primary" : "border-border/60"
            )}
          >
            <Paintbrush className="h-3 w-3" /> 涂
          </button>
          <button
            type="button"
            aria-pressed={erasing}
            disabled={isDisabled}
            onClick={() => setErasing(true)}
            className={cn(
              "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[12.5px]",
              erasing ? "border-primary bg-primary/10 text-primary" : "border-border/60"
            )}
          >
            <Eraser className="h-3 w-3" /> 擦
          </button>
          {BRUSH_SIZES.map((b) => (
            <button
              key={b}
              type="button"
              aria-pressed={brush === b}
              disabled={isDisabled}
              onClick={() => setBrush(b)}
              className={cn(
                "rounded-full border px-2 py-1 text-2xs",
                brush === b ? "border-primary bg-primary/10 text-primary" : "border-border/60"
              )}
            >
              {b === 24 ? "细" : b === 48 ? "中" : "粗"}
            </button>
          ))}
          <button
            type="button"
            disabled={isDisabled || !painted}
            onClick={reset}
            className="inline-flex items-center gap-1 rounded-full border border-border/60 px-2.5 py-1 text-[12.5px] disabled:opacity-40"
          >
            <RotateCcw className="h-3 w-3" /> 重涂
          </button>
        </div>
      </div>

      <div className="relative inline-block max-w-full overflow-hidden rounded-[calc(var(--radius)*1.1)] border border-border/60">
        <img
          ref={imgRef}
          src={imageUrl}
          alt="待改的图"
          onLoad={onImgLoad}
          className="block max-h-[420px] w-auto max-w-full select-none"
          draggable={false}
        />
        <canvas
          ref={canvasRef}
          data-mask-canvas
          className={cn(
            // touch-action:none —— 不禁掉浏览器平移,手指一动就是滚页面,根本涂不上
            "absolute inset-0 h-full w-full touch-none",
            isDisabled ? "pointer-events-none" : "cursor-crosshair"
          )}
          onPointerDown={(e) => {
            if (isDisabled) return;
            drawing.current = true;
            lastPt.current = null; // 新的一笔:先落点,不连上一笔
            // setPointerCapture 在某些 pointerId 上会抛(鼠标合成事件/自动化环境),
            // 抛了就整个 handler 中断 = 一笔都画不上。捕获只是"手指移出元素还能继续画"
            // 的优化,失败不该连累落笔本身。
            try {
              e.currentTarget.setPointerCapture(e.pointerId);
            } catch {
              /* 没有捕获也能画,只是移出元素会断笔 */
            }
            paintAt(e.clientX, e.clientY);
          }}
          onPointerMove={(e) => drawing.current && paintAt(e.clientX, e.clientY)}
          onPointerUp={end}
          onPointerLeave={end}
          // ★触屏:浏览器处理滚动/手势时会派 pointercancel —— 不接的话 drawing/lastPt 留脏,
          //   下一次移动会莫名其妙接着上一笔画(codex 十二轮)。
          onPointerCancel={end}
          // ★触屏:浏览器处理滚动/手势时会派 pointercancel —— 不接的话 drawing/lastPt 留脏,
          //   下一次移动会莫名其妙接着上一笔画(codex 十二轮)。
        />
      </div>

      {disabledReason ? (
        <p
          data-testid="mask-disabled-reason"
          className="text-[12px] text-amber-600 dark:text-amber-400"
        >
          {disabledReason}
        </p>
      ) : (
        !painted && (
          <p className="text-[12px] text-muted-foreground">
            不涂 = 整张图按指令重绘;涂了 = 只重绘涂过的地方。
          </p>
        )
      )}
    </div>
  );
});
